import { Button } from '@mui/material';
import { GetLci3ReportParams } from '@components/Dashboard/dashboardSlice/campaignsApi';
import { stringify } from 'qs';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import {
  Campaign,
  GetCampaignParams,
  useAttachments,
} from '@features/campaigns';
import CustomReportsDownload from '@components/shared/Reports/CustomReportsDownload';
import { useState } from 'react';
import { downloadFromApi } from '@utils/download';
import { useCurrentUser } from '@features/users';
import { VisitationLciReportDownloadContainer } from '@components/shared/Reports/VisitationLciReportDownloadButton';
import { BaseDownloadModal, BaseDownloadModalProps } from './BaseDownloadModal';
import { VisitationExecutiveReportDownload } from '@components/shared/Reports/VisitationExecutiveReportDownload';

export type ReportDownloadModalProps = {
  campaignName: Campaign['name'];
  campaignId: Campaign['id'];
  getCampaignParams: GetCampaignParams;
  reportType: Lci3Report['reportType'];
  reportId: Lci3Report['id'];
  salesReportId: Lci3Report['id'] | null;
  channel?: string;
  renderOpener: BaseDownloadModalProps['renderOpener'];
  enableSalesDownloads?: boolean;
};

export const ReportDownloadModal = (props: ReportDownloadModalProps) => {
  const {
    getCampaignParams,
    channel,
    campaignName,
    campaignId,
    reportId,
    reportType,
    salesReportId,
  } = props;

  const queryParams: Omit<GetLci3ReportParams, 'id'> = {
    includeUnapproved: getCampaignParams.include_unapproved,
    channel: channel ?? undefined,
  };

  const reportEndpoint = reportType === 'custom' ? 'custom_reports' : 'reports';

  const downloadFlatReport = () => {
    downloadFromApi(
      `/api/v3/campaigns/${campaignId}/${reportEndpoint}/${reportId}/flat?${stringify(
        queryParams
      )}`
    );
  };

  const downloadPancakeReport = () => {
    downloadFromApi(
      `/api/v3/campaigns/${campaignId}/${reportEndpoint}/${reportId}/pancake?${stringify(
        queryParams
      )}`
    );
  };

  const downloadSummaryReport = () => {
    downloadFromApi(
      `/api/v3/campaigns/${campaignId}/${reportEndpoint}/${reportId}/summary?${stringify(
        queryParams
      )}`
    );
  };

  const downloadRsaSummaryReport = () => {
    downloadFromApi(
      `/api/v3/campaigns/${campaignId}/${reportEndpoint}/${salesReportId}/sales_summary?${stringify(
        queryParams
      )}`
    );
  };

  const downloadRsaPancakeReport = () => {
    downloadFromApi(
      `/api/v3/campaigns/${campaignId}/${reportEndpoint}/${salesReportId}/sales_pancake?${stringify(
        queryParams
      )}`
    );
  };

  const [isOpen, setIsOpen] = useState(false);
  const { attachments } = useAttachments({ campaignId, skipRequest: !isOpen });
  const { role: userRole } = useCurrentUser();

  const salesDownloadOptions: BaseDownloadModalProps['downloadOptions'] =
    props.enableSalesDownloads
      ? [
          {
            name: 'rsaSummary',
            group: 'Sales Attribution',
            label: 'Table (.xlsx)',
            onClick: downloadRsaSummaryReport,
          },
          {
            name: 'rsaPancake',
            group: 'Sales Attribution',
            label: 'Pancake (.xlsx)',
            internal: true,
            onClick: downloadRsaPancakeReport,
          },
        ]
      : [];

  return (
    <BaseDownloadModal
      campaignId={campaignId}
      campaignName={campaignName}
      renderOpener={props.renderOpener}
      isOpen={isOpen}
      onCloserClick={() => setIsOpen(false)}
      onOpenerClick={() => setIsOpen(true)}
      footer={
        attachments.length > 0 ? (
          <CustomReportsDownload customReports={attachments} />
        ) : null
      }
      downloadOptions={[
        {
          name: 'summary',
          group: 'Data Output',
          label: 'Summary (.xlsx)',
          onClick: downloadSummaryReport,
        },
        {
          name: 'flat',
          group: 'Data Output',
          label: 'Standard (.csv)',
          onClick: downloadFlatReport,
        },
        {
          name: 'msrPpt',
          group: 'Presentation',
          component: (
            <VisitationLciReportDownloadContainer
              campaignId={campaignId}
              campaignParams={getCampaignParams}
              reportParams={queryParams}
            />
          ),
        },
        {
          name: 'pancake',
          group: 'Data Output',
          label: 'Pancake (.csv)',
          hidden: userRole === 'user',
          component: (
            <Button onClick={downloadPancakeReport} size="large">
              Pancake (.csv)
            </Button>
          ),
        },
        {
          name: 'executiveReport',
          group: 'Presentation',
          component: (
            <VisitationExecutiveReportDownload
              campaignId={campaignId}
              campaignParams={getCampaignParams}
              reportParams={queryParams}
            />
          ),
        },
        ...salesDownloadOptions,
      ]}
    />
  );
};
